import {
  chakra,
  forwardRef,
  ResponsiveValue,
  type SystemStyleObject,
} from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import {
  type TableHTMLAttributes,
  type DetailedHTMLProps,
  useMemo,
} from 'react';

import { useTableStyles } from '../TableStylesContext';

export type TableV2Props = DetailedHTMLProps<
  TableHTMLAttributes<HTMLTableElement>,
  HTMLTableElement
> & {
  sx?: SystemStyleObject;
  height?: ResponsiveValue<string | number>;
  tableLayout?: SystemStyleObject['tableLayout'];
  borderCollapse?: SystemStyleObject['borderCollapse'];
  borderSpacing?: SystemStyleObject['borderSpacing'];
  captionSide?: SystemStyleObject['captionSide'];
  emptyCells?: SystemStyleObject['emptyCells'];
  verticalAlign?: SystemStyleObject['verticalAlign'];
};

/**
 * A theme-wrapped HTML table.  Use within the Table wrapper component.
 */
export const TableV2 = forwardRef<TableV2Props, 'table'>(
  (
    {
      tableLayout,
      borderCollapse,
      borderSpacing,
      captionSide,
      emptyCells,
      verticalAlign,
      children,
      className,
      ...restProps
    },
    ref,
  ) => {
    const { htmlTable: htmlTableStyles } = useTableStyles() ?? {};

    const __css = useMemo(
      () => ({
        ...htmlTableStyles,
        tableLayout: tableLayout ?? htmlTableStyles?.tableLayout,
        borderCollapse: borderCollapse ?? htmlTableStyles?.borderCollapse,
        borderSpacing: borderSpacing ?? htmlTableStyles?.borderSpacing,
        captionSide: captionSide ?? htmlTableStyles?.captionSide,
        emptyCells: emptyCells ?? htmlTableStyles?.emptyCells,
        verticalAlign: verticalAlign ?? htmlTableStyles?.verticalAlign,
      }),
      [
        htmlTableStyles,
        tableLayout,
        borderCollapse,
        borderSpacing,
        captionSide,
        emptyCells,
        verticalAlign,
      ],
    );

    return (
      <chakra.table
        ref={ref}
        __css={__css}
        className={cx('table', className)}
        {...restProps}
      >
        {children}
      </chakra.table>
    );
  },
);
TableV2.displayName = 'TableV2';
